import { Controller } from '@hotwired/stimulus'
import { loadTurboFrame } from 'helpers/turbo_frames'

// Drives the moment-survey form: Q1 reveals the activity picker (plus Q3 for a
// Yes), the picker frame loads the code roots matching the chosen answer
// (Yes -> working, No -> not_working), and Q3 follows the picked code's root.
export default class extends Controller {
  static targets = [
    'sections',
    'frame',
    'codeError',
    'description',
    'descriptionSection',
    'descriptionError',
    'questionThreePrompt',
    'questionThreeOptions',
    'optionsError',
    'optionDetailError'
  ]
  static values = { workingUrl: String, notWorkingUrl: String }

  connect() {
    // On a 422 re-render the server already rendered the right picker; seeding
    // currentType from the checked radio prevents a src-set from wiping it.
    const checked = this.element.querySelector('input[name="time_study_response[working_status]"]:checked')
    this.currentType = checked ? checked.dataset.timeStudySurveyFormActivityTypeParam : null
    // Likewise for Q3: the server already revealed the block for this root.
    this.questionThreeRootId = this.pickedRootId()
  }

  select(event) {
    this.sectionsTarget.classList.remove('d-none')

    const type = event.params.activityType
    if (type === this.currentType) return

    this.currentType = type
    this.toggleDescriptionSection()
    // This frame reload is a working-status switch, not the user drilling the
    // picker. Its turbo:frame-load must not wipe a code error that previewSubmit
    // may show in the same tick — on slow loads the events race (see spec).
    this.switchingType = true
    loadTurboFrame(this.frameTarget, type === 'working' ? this.workingUrlValue : this.notWorkingUrlValue)
  }

  // We don't ask what a participant was up to while off the clock (GRC-528).
  toggleDescriptionSection() {
    const hidden = this.currentType === 'not_working'
    this.descriptionSectionTarget.classList.toggle('d-none', hidden)
    if (hidden) this.clearDescriptionError()
  }

  clearCodeError() {
    if (this.switchingType) {
      this.switchingType = false
      return
    }
    if (this.hasCodeErrorTarget) this.codeErrorTarget.classList.add('d-none')
  }

  clearDescriptionError() {
    if (this.hasDescriptionErrorTarget) this.descriptionErrorTarget.classList.add('d-none')
  }

  clearOptionErrors() {
    if (this.hasOptionsErrorTarget) this.optionsErrorTarget.classList.add('d-none')
    if (this.hasOptionDetailErrorTarget) this.optionDetailErrorTarget.classList.add('d-none')
  }

  // The root code is the first id in the picker's drilled path.
  pickedRootId() {
    const field = document.getElementById('activity_code_selections_field')
    return (field?.value || '').split(',')[0] || ''
  }

  // Q3's prompt and inputs are configured on the root code, but Q3 lives outside
  // the picker frame — so reveal the blocks matching whichever root is picked.
  // Inputs in the hidden blocks are disabled so they can never post.
  syncQuestionThree() {
    const rootId = this.pickedRootId()
    if (rootId === this.questionThreeRootId) return
    this.questionThreeRootId = rootId

    const prompt =
      this.questionThreePromptTargets.find(el => el.dataset.rootCodeId === rootId) ||
      this.questionThreePromptTargets.find(el => !el.dataset.rootCodeId)
    this.questionThreePromptTargets.forEach(el => el.classList.toggle('d-none', el !== prompt))

    const options = this.questionThreeOptionsTargets.find(el => el.dataset.rootCodeId === rootId)
    this.questionThreeOptionsTargets.forEach(el => {
      const active = el === options
      el.classList.toggle('d-none', !active)
      el.querySelectorAll('input[type="checkbox"]').forEach(box => {
        box.disabled = !active
      })
      el.querySelectorAll('[data-option-detail]').forEach(field => this.resetOptionDetail(field, active))
    })

    this.clearDescriptionError()
    this.clearOptionErrors()
  }

  // A free-text detail only applies while its own option is checked.
  toggleOptionDetail(event) {
    const field = event.target.closest('.ts-survey-option-row')?.querySelector('[data-option-detail]')
    if (field) this.applyOptionDetailState(field, event.target.checked)
    this.clearOptionErrors()
  }

  resetOptionDetail(field, blockActive) {
    const checkbox = field.closest('.ts-survey-option-row')?.querySelector('input[type="checkbox"]')
    this.applyOptionDetailState(field, Boolean(blockActive && checkbox?.checked))
  }

  applyOptionDetailState(field, shown) {
    field.classList.toggle('d-none', !shown)
    field.disabled = !shown
    if (!shown) field.value = ''
  }

  // Preview mode: the form never posts, so mimic the server-side validations
  // and show the same inline errors before revealing the thanks banner.
  previewSubmit() {
    if (!this.element.querySelector('input[name="time_study_response[working_status]"]:checked')) {
      // Q2/Q3 are still hidden at this point, so theirs are the only errors worth raising later.
      this.element.querySelector('.ts-option-cards').classList.add('ts-option-cards--error')
      document.getElementById('ts-q1-error').classList.remove('d-none')
      return
    }

    let valid = true

    if (!document.getElementById('activity_code_id_field')?.value) {
      this.codeErrorTarget.classList.remove('d-none')
      valid = false
    }

    if (this.currentType !== 'not_working' && !this.questionThreeAnswered()) valid = false

    if (!valid) return

    document.getElementById('response_form').classList.add('d-none')
    document.getElementById('time_study_preview_thanks').classList.remove('d-none')
  }

  // Mirrors Response's Q3 validations: the narrative is always required, and a root
  // offering options also needs one checked plus free text on any that requires it.
  // Every failing check is raised at once rather than one per submit.
  questionThreeAnswered() {
    let answered = true

    if (!this.descriptionTarget.value.trim()) {
      this.descriptionErrorTarget.classList.remove('d-none')
      answered = false
    }

    const options = this.questionThreeOptionsTargets.find(el => !el.classList.contains('d-none'))
    if (!options) return answered

    const checked = [...options.querySelectorAll('input[type="checkbox"]')].filter(box => box.checked)
    if (checked.length === 0) {
      this.optionsErrorTarget.classList.remove('d-none')
      return false
    }

    const missingDetail = checked.some(box => {
      const field = box.closest('.ts-survey-option-row')?.querySelector('[data-option-detail]')
      return field && !field.value.trim()
    })
    if (missingDetail) {
      this.optionDetailErrorTarget.classList.remove('d-none')
      answered = false
    }

    return answered
  }
}
